import type { ColumnFiltersState, PaginationState, SortingState } from '@tanstack/react-table';
import type { Dispatch, SetStateAction } from 'react';
import { useMemo, useState } from 'react';
import { buildServerTableFilterRecord } from './BaseHtmlTable';

export type ServerTableFilterStateResult = {
  mergedRecord: Record<string, unknown>;
  pagination: PaginationState;
  setPagination: Dispatch<SetStateAction<PaginationState>>;
  sorting: SortingState;
  setSorting: Dispatch<SetStateAction<SortingState>>;
  columnFilters: ColumnFiltersState;
  setColumnFilters: Dispatch<SetStateAction<ColumnFiltersState>>;
  serverPagination: PaginationState;
  onServerPaginationChange: Dispatch<SetStateAction<PaginationState>>;
  serverSorting: SortingState;
  onServerSortingChange: Dispatch<SetStateAction<SortingState>>;
  serverColumnFilters: ColumnFiltersState;
  onServerColumnFiltersChange: Dispatch<SetStateAction<ColumnFiltersState>>;
};

type Options = {
  initialPagination?: PaginationState;
  initialSorting?: SortingState;
  initialColumnFilters?: ColumnFiltersState;
};

export function useServerTableFilterState(
  columnDefs: any[],
  baseRecord: Record<string, unknown>,
  options?: Options
): ServerTableFilterStateResult {
  const [pagination, setPagination] = useState<PaginationState>(
    options?.initialPagination ?? { pageIndex: 0, pageSize: 100 }
  );
  const [sorting, setSorting] = useState<SortingState>(options?.initialSorting ?? []);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
    options?.initialColumnFilters ?? []
  );

  // Merge the base record with the pagination, sorting, and column filters to supply to the API hooks
  const mergedRecord = useMemo(() => {
    const flat = buildServerTableFilterRecord(pagination, columnFilters, sorting, columnDefs);
    return { ...baseRecord, ...flat };
  }, [baseRecord, pagination, columnFilters, sorting, columnDefs]);

  return {
    mergedRecord,
    pagination,
    setPagination,
    sorting,
    setSorting,
    columnFilters,
    setColumnFilters,
    serverPagination: pagination,
    onServerPaginationChange: setPagination,
    serverSorting: sorting,
    onServerSortingChange: setSorting,
    serverColumnFilters: columnFilters,
    onServerColumnFiltersChange: setColumnFilters,
  };
}
